import React, {
  Suspense, useEffect, useMemo, useState, useCallback,
} from 'react';
import './App.scss';
import { ExLoader, LoaderSize, LoaderVariant } from '@boomi/exosphere';
// @ts-ignore
import { AISearch } from '@boomi/magnetosphere';
import {
  createBrowserRouter,
  createHashRouter,
  RouterProvider,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
  Outlet,
} from 'react-router-dom';
import {
  UserResponse,
} from './common/interfaces';
import Auth from '@services/auth';
import Util from '@services/util';
import BoomiAI from './pages/BoomiAI/BoomiAI';
import Rest from '@services/rest';
import './components/Onboarding/Onboarding.scss';
import Features from '@utils/features';
import DataCache from '@utils/data/dataCache';
import BoomiContextProvider, { useBoomiContext } from './context/BoomiContext';
import BoomiAgentFoundationContextProvider, { useBoomiAgentFoundationContext } from './context/BoomiAgentFoundationContext';
import redirectToPlatform from '@services/general';
import { AppConstants } from './common';
import TimeOutOfSyncWarningMessage from './components/TimeOutOfSyncWarningMessage/TimeOutOfSyncWarningMessage';
import RouteGuard from './RouteGuard';
import UserPrivilege from '@utils/data/userPrivileges';
import { getSubNavMenu } from '@utils/general/general';
import LeftMenuManager from '@components/LeftMenuManager/LeftMenuManager';
import MastHead from '@components/Header/MastHead';
import { AppFeatureProvider } from './context/AppFeatureContext';
import SessionErrorBoundary from '@components/ErrorBoundary/SessionErrorBoundary';

const Login = React.lazy(() => import(/* webpackChunkName: "Login" */'./pages/Login/Login'));
const TermsAndConditions = React.lazy(() => import(/* webpackChunkName: "TermsAndConditions" */'./components/Onboarding/TermsAndConditions'));
const Footer = React.lazy(() => import(/* webpackChunkName: "Footer" */'./components/Footer/Footer'));
const AgentGardenHomePage = React.lazy(() => import(/* webpackChunkName: "AgentGardenHomePage" */'@pages/AgentGardenHomePage/AgentGardenHomePage'));
const AgentDesignerPage = React.lazy(() => import(/* webpackChunkName: "AgentDesigner" */'@pages/AgentDesigner/AgentDesigner'));
const ToolsListPage = React.lazy(() => import(/* webpackChunkName: "ToolsList" */'@pages/ToolsList/ToolsList'));
const ChatPage = React.lazy(() => import(/* webpackChunkName: "Chat" */'@pages/Chat/Chat'));
const AddTools = React.lazy(() => import(/* webpackChunkName: "AddTools" */'@components/ToolManagement/AddTools'));
const AgentsListPage = React.lazy(() => import(/* webpackChunkName: "AgentsList" */'@pages/AgentsList/AgentsList'));
const McpWizard = React.lazy(() => import(/* webpackChunkName: "SourcesWizard" */'@components/McpWizard/McpWizard'));
const AcpWizard = React.lazy(() => import(/* webpackChunkName: "AcpWizard" */'@components/AcpWizard/AcpWizard'));
const SourcesPage = React.lazy(() => import(/* webpackChunkName: "Sources" */'@pages/Sources/Sources'));
const McpServerCatalog = React.lazy(() => import(/* webpackChunkName: "McpServerCatalog" */'@components/McpServerCatalog/McpServerCatalog'));
const EnvironmentsPage = React.lazy(() => import(/* webpackChunkName: "Environments" */'@pages/Environments/Environments'));
const PackagesDeploymentsPage = React.lazy(() => import(/* webpackChunkName: "PackagesDeployments" */'@pages/PackagesDeployments/PackagesDeployments'));
const ModelsPage = React.lazy(() => import(/* webpackChunkName: "Models" */'@components/ModelsManagement').then((m) => ({ default: m.ModelsPage })));

function Loader() {
  return (
    <div className="full-page-loader" data-testid="loader">
      <ExLoader size={LoaderSize.MEDIUM} variant={LoaderVariant.SPINNER} />
    </div>
  );
}

type RouteResetWrapperProps = {
  children: React.ReactNode;
};

export function RouteResetWrapper({ children }: Readonly<RouteResetWrapperProps>) {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  // To reset state on mode change (create, edit)
  return <React.Fragment key={mode}>{children}</React.Fragment>;
}

const isDevelopmentMode = () => Util.isDevelopmentMode();

const basePath = isDevelopmentMode()
  ? '/BoomiAI/boomi-ai-ui'
  : '';

const redirectToPlatformWrapper = () => {
  redirectToPlatform();
};

function AI({
  isPageLoading,
  isLoggedIn,
  updateTNC,
  dataCache,
  userResponse,
  showDevLoginorRedirectToPlatformLogin,
} :any) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  document.title = 'Boomi GPT';
  useEffect(() => {
    if (!Util.isValidToken()) {
      showDevLoginorRedirectToPlatformLogin();
    }
  }, [showDevLoginorRedirectToPlatformLogin]);

  const handleRedirectToAgentGarden = () => {
    document.title = 'Agent Garden';
    if (userResponse?.user) {
      navigate({
        pathname: '/home',
        search: searchParams.toString(),
      }, {
        state: { user: userResponse.user },
      });
    }
  };
  function RedirectToAgentGarden() {
    useEffect(() => {
      handleRedirectToAgentGarden();
    }, []);

    return null; // Render nothing
  }

  return (
    <div id="boomi-ai-gpt" className="chatHome">
      {isPageLoading
            && (
            <div className="full-page-loader" data-testid="loader">
              <ExLoader size={LoaderSize.MEDIUM} variant={LoaderVariant.SPINNER} />
            </div>
            )}
      {isLoggedIn && !isPageLoading
            && (
            <div className="landing-page-container">
              {(!Features.isTnCFlagEnabled(userResponse?.user)
                  || !Features.isAiFlagEnabled(userResponse?.user))
                      && (
                      <div className="termsandcond-wrapper">
                        <Suspense fallback={<Loader />}>
                          <TermsAndConditions
                            user={userResponse}
                            dataCache={dataCache}
                            tNc={updateTNC}
                          />
                        </Suspense>
                      </div>
                      )}
              {Features.isTnCFlagEnabled(userResponse?.user)
                      && Features.isAiFlagEnabled(userResponse?.user)
                      && !Features.isAgentGardenFlagEnabled(userResponse?.user)
                      && (
                      <div className="legacy-gpt-page-container">
                        <div className="legacy-gpt-container">
                          <div className="chatHome__content legacy-gpt">
                            <BoomiAI userDetails={userResponse} />
                          </div>
                        </div>
                      </div>
                      )}

              {Features.isTnCFlagEnabled(userResponse?.user)
              && Features.isAgentGardenFlagEnabled(userResponse?.user)
              && Features.isAiFlagEnabled(userResponse?.user)
              && (<RedirectToAgentGarden />)}

              {/* <Suspense fallback={<Loader />}>
                <Footer />
              </Suspense> */}
            </div>
            )}
    </div>
  );
}

function RenderRoute({
  isPageLoading,
  updateTNC,
  dataCache,
  userResponse,
  isLoggedIn,
  getNewJWTorRedirectToPlatform,
  settingData,
}: any) {
  const location = useLocation();

  if (location.pathname.startsWith('/home')) {
    return (
      <SessionErrorBoundary section="AgentGardenHome">
        <RouteGuard
          condition={Features.isAgentGardenFlagEnabled(userResponse?.user)
              && Features.isAiFlagEnabled(userResponse?.user)}
          redirectTo="/"
          userResponse={userResponse}
        >
          <Suspense fallback={<Loader />}>
            <AgentGardenHomePage
              userResponse={userResponse}
              settingData={settingData}
              dataCache={dataCache}
            />
          </Suspense>
        </RouteGuard>
      </SessionErrorBoundary>
    );
  }
  if (location.pathname.startsWith('/agents')) {
    return (
      <RouteGuard
        condition={Features.isAgentGardenFlagEnabled(userResponse?.user)
            && Features.isAiFlagEnabled(userResponse?.user)}
        redirectTo="/"
        userResponse={userResponse}
      >
        <Suspense fallback={<Loader />}>
          <AgentsListPage />
        </Suspense>
      </RouteGuard>
    );
  }
  if (location.pathname.startsWith('/agent-designer')) {
    return (
      <RouteGuard
        condition={Features.isAgentGardenFlagEnabled(userResponse?.user)
            && Features.isAiFlagEnabled(userResponse?.user)}
        redirectTo="/"
        requiredPrivileges={[UserPrivilege.AGENT_CREATE, UserPrivilege.AGENT_EDIT]}
        userResponse={userResponse}
      >
        <Suspense fallback={<Loader />}>
          <AgentDesignerPage userResponse={userResponse} />
        </Suspense>
      </RouteGuard>
    );
  }
  if (location.pathname.startsWith('/tools')) {
    return (
      <RouteGuard
        condition={Features.isAgentGardenFlagEnabled(userResponse?.user)
            && Features.isAiFlagEnabled(userResponse?.user)}
        redirectTo="/"
        requiredPrivileges={[UserPrivilege.AGENT_CREATE, UserPrivilege.AGENT_EDIT]}
        userResponse={userResponse}
      >
        <Suspense fallback={<Loader />}>
          <ToolsListPage />
        </Suspense>
      </RouteGuard>
    );
  }
  if (location.pathname.startsWith('/chat')) {
    return (
      <RouteGuard
        condition={Features.isAgentGardenFlagEnabled(userResponse?.user)
            && Features.isAiFlagEnabled(userResponse?.user)}
        redirectTo="/"
        requiredPrivileges={[UserPrivilege.AGENT_INTERACT]}
        userResponse={userResponse}
      >
        <Suspense fallback={<Loader />}>
          <ChatPage userResponse={userResponse} />
        </Suspense>
      </RouteGuard>
    );
  }
  if (location.pathname.startsWith('/add-tools')) {
    return (
      <RouteGuard
        condition={Features.isAgentGardenFlagEnabled(userResponse?.user)
            && Features.isAiFlagEnabled(userResponse?.user)}
        redirectTo="/"
        requiredPrivileges={[UserPrivilege.AGENT_CREATE, UserPrivilege.AGENT_EDIT]}
        userResponse={userResponse}
      >
        <Suspense fallback={<Loader />}>
          <RouteResetWrapper>
            <AddTools />
          </RouteResetWrapper>
        </Suspense>
      </RouteGuard>
    );
  }
  if (location.pathname.startsWith('/explore-mcp-servers')) {
    return (
      <RouteGuard
        condition={
          Features.isAgentGardenFlagEnabled(userResponse?.user)
          && Features.isAiFlagEnabled(userResponse?.user)
        }
        redirectTo="/"
        requiredPrivileges={[
          UserPrivilege.CONTROL_PLANE_ADMIN,
          UserPrivilege.CONTROL_PLANE_USER,
        ]}
        userResponse={userResponse}
        privilegeCheckMode="OR"
      >
        <Suspense fallback={<Loader />}>
          <McpServerCatalog />
        </Suspense>
      </RouteGuard>
    );
  }
  if (location.pathname.startsWith('/sources')) {
    return (
      <RouteGuard
        condition={
          Features.isAgentGardenFlagEnabled(userResponse?.user)
          && Features.isAiFlagEnabled(userResponse?.user)
          && Features.isSourcesManagementEnabled()
        }
        redirectTo="/"
        requiredPrivileges={[
          UserPrivilege.AGENT_CREATE,
          UserPrivilege.AGENT_EDIT,
        ]}
        userResponse={userResponse}
      >
        <Suspense fallback={<Loader />}>
          <SourcesPage />
        </Suspense>
      </RouteGuard>
    );
  }
  if (location.pathname.startsWith('/add-source')) {
    return (
      <RouteGuard
        condition={Features.isAgentGardenFlagEnabled(userResponse?.user)
            && Features.isAiFlagEnabled(userResponse?.user)
            && Features.isSourcesManagementEnabled()}
        redirectTo="/"
        requiredPrivileges={[UserPrivilege.AGENT_CREATE, UserPrivilege.AGENT_EDIT]}
        userResponse={userResponse}
      >
        <Suspense fallback={<Loader />}>
          <RouteResetWrapper>
            <McpWizard />
          </RouteResetWrapper>
        </Suspense>
      </RouteGuard>
    );
  }
  if (location.pathname.startsWith('/add-acp-source')) {
    return (
      <RouteGuard
        condition={Features.isAgentGardenFlagEnabled(userResponse?.user)
            && Features.isAiFlagEnabled(userResponse?.user)
            && Features.isSourcesManagementEnabled()}
        redirectTo="/"
        requiredPrivileges={[
          UserPrivilege.AGENT_CREATE,
          UserPrivilege.AGENT_EDIT,
        ]}
        privilegeCheckMode="AND"
        userResponse={userResponse}
      >
        <RouteGuard
          condition
          redirectTo="/"
          requiredPrivileges={[]}
          privilegeCheckMode="OR"
          userResponse={userResponse}
        >
          <Suspense fallback={<Loader />}>
            <AcpWizard />
          </Suspense>
        </RouteGuard>
      </RouteGuard>
    );
  }
  if (location.pathname.startsWith('/environments')) {
    return (
      <RouteGuard
        condition={Features.isAgentGardenFlagEnabled(userResponse?.user)
            && Features.isAiFlagEnabled(userResponse?.user)}
        redirectTo="/"
        requiredPrivileges={[UserPrivilege.AGENT_CREATE, UserPrivilege.AGENT_EDIT]}
        userResponse={userResponse}
      >
        <Suspense fallback={<Loader />}>
          <EnvironmentsPage />
        </Suspense>
      </RouteGuard>
    );
  }

  if (location.pathname.startsWith('/packages-deployments')) {
    return (
      <RouteGuard
        condition={Features.isAgentGardenFlagEnabled(userResponse?.user)
            && Features.isAiFlagEnabled(userResponse?.user)}
        redirectTo="/"
        userResponse={userResponse}
      >
        <Suspense fallback={<Loader />}>
          <PackagesDeploymentsPage />
        </Suspense>
      </RouteGuard>
    );
  }

  if (location.pathname.startsWith('/models')) {
    return (
      <RouteGuard
        condition={Features.isAgentGardenFlagEnabled(userResponse?.user)
            && Features.isAiFlagEnabled(userResponse?.user)
            && Features.isAgentGardenByomEnabled(userResponse?.user)}
        redirectTo="/"
        requiredPrivileges={[UserPrivilege.AGENT_CREATE, UserPrivilege.AGENT_EDIT]}
        userResponse={userResponse}
      >
        <Suspense fallback={<Loader />}>
          <ModelsPage />
        </Suspense>
      </RouteGuard>
    );
  }

  return (
    <AI
      isPageLoading={isPageLoading}
      isLoggedIn={isLoggedIn}
      updateTNC={updateTNC}
      dataCache={dataCache}
      userResponse={userResponse}
      showDevLoginorRedirectToPlatformLogin={getNewJWTorRedirectToPlatform}
    />
  );
}
const accountId = Util.getCookie('ACCOUNT_ID');
const goBackLink = Util.isDevelopmentMode() ? 'ai' : `/BoomiAI.html#ai;accountId=${
  accountId as string
}`;

// Layout component for routes
function RouterLayout({
  userResponse,
  isMobileView,
  newMastheadEnabled,
  subNavMenu,
  searchIconEventHandler,
  isClicked,
  closeBtnEventHandler,
  isPageLoading,
}: any) {
  let appBodyClass = 'app-body';
  if (isMobileView && newMastheadEnabled) {
    appBodyClass = 'app-body ex-mt-2x-large has-mobile-nav';
  }

  let showLeftNav = false;
  if (isMobileView) {
    showLeftNav = !newMastheadEnabled;
  } else {
    showLeftNav = true;
  }

  let containerClassName = '';
  if (Features.isAgentGardenFlagEnabled(userResponse?.user)) {
    containerClassName = newMastheadEnabled ? appBodyClass : `${appBodyClass} old-header`;
  }

  return (
    <>
      <MastHead
        subNavMenu={subNavMenu}
        userResponse={userResponse}
        isMobileView={isMobileView}
        newMastheadEnabled={newMastheadEnabled}
        searchIconEventHandler={searchIconEventHandler}
      />
      <AISearch
        key={isClicked ? 'open' : 'closed'}
        openSearchPanel={isClicked}
        serializeMode={'backend' as any}
        userIsAuthenticated
        topMargin={56}
        closeBtnEventHandler={closeBtnEventHandler}
      />
      <div
        className={containerClassName}
      >
        {isPageLoading ? (
          <Loader />
        ) : (
          <>
            {Features.isAgentGardenFlagEnabled(userResponse?.user)
              && showLeftNav && <LeftMenuManager userResponse={userResponse} />}
            <Outlet />
          </>
        )}
      </div>
    </>
  );
}

function DevRouting({
  isPageLoading,
  updateTNC,
  dataCache,
  userResponse,
  isLoggedIn,
  subNavMenu,
  getNewJWTorRedirectToPlatform,
  searchIconEventHandler,
  isClicked,
  closeBtnEventHandler,
  settingData,
}: any) {
  const {
    setUserPrivileges,
    isMobileView,
  } = useBoomiAgentFoundationContext();
  const { setIsDarkMode } = useBoomiContext();
  const userPrivileges = useMemo(() => dataCache.getAccountPrivileges(), [dataCache]);
  useEffect(() => {
    setUserPrivileges(userPrivileges);
  }, [userPrivileges, setUserPrivileges]);
  const newMastheadEnabled = true;

  useEffect(() => {
    setIsDarkMode(Features.isDarkModeEnabled(userResponse?.user));
  }, [userResponse, setIsDarkMode]);

  const router = useMemo(() => createBrowserRouter(
    [
      {
        element: (
          <RouterLayout
            userResponse={userResponse}
            isMobileView={isMobileView}
            newMastheadEnabled={newMastheadEnabled}
            subNavMenu={subNavMenu}
            searchIconEventHandler={searchIconEventHandler}
            isClicked={isClicked}
            closeBtnEventHandler={closeBtnEventHandler}
            isPageLoading={isPageLoading}
          />
        ),
        children: [
          {
            path: '/',
            element: (
              <AI
                isPageLoading={isPageLoading}
                isLoggedIn={isLoggedIn}
                updateTNC={updateTNC}
                dataCache={dataCache}
                userResponse={userResponse}
                showDevLoginorRedirectToPlatformLogin={
                  getNewJWTorRedirectToPlatform
                }
              />
            ),
          },
          {
            path: '/agent-designer',
            element: (
              <RouteGuard
                condition={
                  Features.isAgentGardenFlagEnabled(userResponse?.user)
                  && Features.isAiFlagEnabled(userResponse?.user)
                }
                redirectTo="/"
                requiredPrivileges={[
                  UserPrivilege.AGENT_CREATE,
                  UserPrivilege.AGENT_EDIT,
                ]}
                userResponse={userResponse}
              >
                <Suspense fallback={<Loader />}>
                  <AgentDesignerPage userResponse={userResponse} />
                </Suspense>
              </RouteGuard>
            ),
          },
          {
            path: '/tools',
            element: (
              <RouteGuard
                condition={
                  Features.isAgentGardenFlagEnabled(userResponse?.user)
                  && Features.isAiFlagEnabled(userResponse?.user)
                }
                redirectTo="/"
                requiredPrivileges={[
                  UserPrivilege.AGENT_CREATE,
                  UserPrivilege.AGENT_EDIT,
                ]}
                userResponse={userResponse}
              >
                <Suspense fallback={<Loader />}>
                  <ToolsListPage />
                </Suspense>
              </RouteGuard>
            ),
          },
          {
            path: '/chat',
            element: (
              <RouteGuard
                condition={
                  Features.isAgentGardenFlagEnabled(userResponse?.user)
                  && Features.isAiFlagEnabled(userResponse?.user)
                }
                redirectTo="/"
                requiredPrivileges={[UserPrivilege.AGENT_INTERACT]}
                userResponse={userResponse}
              >
                <Suspense fallback={<Loader />}>
                  <ChatPage userResponse={userResponse} />
                </Suspense>
              </RouteGuard>
            ),
          },
          {
            path: '/home',
            element: (
              <SessionErrorBoundary section="AgentGardenHome">
                <RouteGuard
                  condition={
                    Features.isAgentGardenFlagEnabled(userResponse?.user)
                    && Features.isAiFlagEnabled(userResponse?.user)
                  }
                  redirectTo="/"
                  userResponse={userResponse}
                >
                  <Suspense fallback={<Loader />}>
                    <AgentGardenHomePage
                      dataCache={dataCache}
                      userResponse={userResponse}
                      settingData={settingData}
                    />
                  </Suspense>
                </RouteGuard>
              </SessionErrorBoundary>
            ),
          },
          {
            path: '/add-tools',
            element: (
              <RouteGuard
                condition={
                  Features.isAgentGardenFlagEnabled(userResponse?.user)
                  && Features.isAiFlagEnabled(userResponse?.user)
                }
                redirectTo="/"
                requiredPrivileges={[
                  UserPrivilege.AGENT_CREATE,
                  UserPrivilege.AGENT_EDIT,
                ]}
                userResponse={userResponse}
              >
                <Suspense fallback={<Loader />}>
                  <RouteResetWrapper>
                    <AddTools />
                  </RouteResetWrapper>
                </Suspense>
              </RouteGuard>
            ),
          },
          {
            path: '/add-source',
            element: (
              <RouteGuard
                condition={
                  Features.isAgentGardenFlagEnabled(userResponse?.user)
                  && Features.isAiFlagEnabled(userResponse?.user)
                  && Features.isSourcesManagementEnabled()
                }
                redirectTo="/"
                requiredPrivileges={[
                  UserPrivilege.AGENT_CREATE,
                  UserPrivilege.AGENT_EDIT,
                ]}
                userResponse={userResponse}
              >
                <Suspense fallback={<Loader />}>
                  <RouteResetWrapper>
                    <McpWizard />
                  </RouteResetWrapper>
                </Suspense>
              </RouteGuard>
            ),
          },
          {
            path: '/add-acp-source',
            element: (
              <RouteGuard
                condition={
                  Features.isAgentGardenFlagEnabled(userResponse?.user)
                  && Features.isAiFlagEnabled(userResponse?.user)
                  && Features.isSourcesManagementEnabled()
                }
                redirectTo="/"
                requiredPrivileges={[
                  UserPrivilege.AGENT_CREATE,
                  UserPrivilege.AGENT_EDIT,
                ]}
                privilegeCheckMode="AND"
                userResponse={userResponse}
              >
                <RouteGuard
                  condition
                  redirectTo="/"
                  requiredPrivileges={[]}
                  privilegeCheckMode="OR"
                  userResponse={userResponse}
                >
                  <Suspense fallback={<Loader />}>
                    <AcpWizard />
                  </Suspense>
                </RouteGuard>
              </RouteGuard>
            ),
          },
          {
            path: '/agents',
            element: (
              <RouteGuard
                condition={
                  Features.isAgentGardenFlagEnabled(userResponse?.user)
                  && Features.isAiFlagEnabled(userResponse?.user)
                }
                redirectTo="/"
                userResponse={userResponse}
              >
                <Suspense fallback={<Loader />}>
                  <AgentsListPage />
                </Suspense>
              </RouteGuard>
            ),
          },
          {
            path: '/explore-mcp-servers',
            element: (
              <RouteGuard
                condition={
                  Features.isAgentGardenFlagEnabled(userResponse?.user)
                  && Features.isAiFlagEnabled(userResponse?.user)
                }
                redirectTo="/"
                requiredPrivileges={[
                  UserPrivilege.CONTROL_PLANE_ADMIN,
                  UserPrivilege.CONTROL_PLANE_USER,
                ]}
                userResponse={userResponse}
                privilegeCheckMode="OR"
              >
                <Suspense fallback={<Loader />}>
                  <McpServerCatalog />
                </Suspense>
              </RouteGuard>
            ),
          },
          {
            path: '/sources',
            element: (
              <RouteGuard
                condition={
                  Features.isAgentGardenFlagEnabled(userResponse?.user)
                  && Features.isAiFlagEnabled(userResponse?.user)
                  && Features.isSourcesManagementEnabled()
                }
                redirectTo="/"
                requiredPrivileges={[
                  UserPrivilege.AGENT_CREATE,
                  UserPrivilege.AGENT_EDIT,
                ]}
                userResponse={userResponse}
              >
                <Suspense fallback={<Loader />}>
                  <SourcesPage />
                </Suspense>
              </RouteGuard>
            ),
          },
          {
            path: '/packages-deployments',
            element: (
              <RouteGuard
                condition={
                  Features.isAgentGardenFlagEnabled(userResponse?.user)
                  && Features.isAiFlagEnabled(userResponse?.user)
                }
                redirectTo="/"
                userResponse={userResponse}
              >
                <Suspense fallback={<Loader />}>
                  <PackagesDeploymentsPage />
                </Suspense>
              </RouteGuard>
            ),
          },
          {
            path: '/environments',
            element: (
              <RouteGuard
                condition={
                  Features.isAgentGardenFlagEnabled(userResponse?.user)
                  && Features.isAiFlagEnabled(userResponse?.user)
                }
                redirectTo="/"
                requiredPrivileges={[UserPrivilege.AGENT_CREATE, UserPrivilege.AGENT_EDIT]}
                userResponse={userResponse}
              >
                <Suspense fallback={<Loader />}>
                  <EnvironmentsPage />
                </Suspense>
              </RouteGuard>
            ),
          },
          {
            path: '/models',
            element: (
              <RouteGuard
                condition={
                  Features.isAgentGardenFlagEnabled(userResponse?.user)
                  && Features.isAiFlagEnabled(userResponse?.user)
                  && Features.isAgentGardenByomEnabled(userResponse?.user)
                }
                redirectTo="/"
                requiredPrivileges={[UserPrivilege.AGENT_CREATE, UserPrivilege.AGENT_EDIT]}
                userResponse={userResponse}
              >
                <Suspense fallback={<Loader />}>
                  <ModelsPage />
                </Suspense>
              </RouteGuard>
            ),
          },
          {
            path: '*',
            element: <Navigate to="/" />,
          },
        ],
      },
    ],
    { basename: basePath },
  ), [
    isPageLoading,
    isLoggedIn,
    updateTNC,
    dataCache,
    userResponse,
    getNewJWTorRedirectToPlatform,
    settingData,
    isMobileView,
    newMastheadEnabled,
    subNavMenu,
    searchIconEventHandler,
    isClicked,
    closeBtnEventHandler,
  ]);

  document.title = 'Boomi GPT';

  return (
    <div>
      <RouterProvider router={router} useTransitions={false} />
      <Suspense fallback={<Loader />}>
        <div className="hide-footer-visually">
          <Footer />
        </div>
      </Suspense>
    </div>
  );
}

function ProdRouting({
  isPageLoading,
  updateTNC,
  dataCache,
  userResponse,
  isLoggedIn,
  subNavMenu,
  getNewJWTorRedirectToPlatform,
  searchIconEventHandler,
  isClicked,
  closeBtnEventHandler,
  settingData,
}: any) {
  const {
    setUserPrivileges,
    isMobileView,
  } = useBoomiAgentFoundationContext();
  const { setIsDarkMode } = useBoomiContext();
  const userPrivileges = useMemo(() => dataCache.getAccountPrivileges(), [dataCache]);
  useEffect(() => {
    setUserPrivileges(userPrivileges);
  }, [userPrivileges, setUserPrivileges]);
  const newMastheadEnabled = true;

  useEffect(() => {
    setIsDarkMode(Features.isDarkModeEnabled(userResponse?.user));
  }, [userResponse, isLoggedIn, setIsDarkMode]);

  const subNavMenuWithLeftNav = structuredClone(subNavMenu);
  subNavMenuWithLeftNav[0].submenus = [...AppConstants.LEFT_NAV_SUBMENUS];

  const router = useMemo(() => createHashRouter([
    {
      element: (
        <RouterLayout
          userResponse={userResponse}
          isMobileView={isMobileView}
          newMastheadEnabled={newMastheadEnabled}
          subNavMenu={subNavMenu}
          searchIconEventHandler={searchIconEventHandler}
          isClicked={isClicked}
          closeBtnEventHandler={closeBtnEventHandler}
          isPageLoading={isPageLoading}
        />
      ),
      children: [
        {
          path: '*',
          element: (
            <RenderRoute
              isPageLoading={isPageLoading}
              updateTNC={updateTNC}
              dataCache={dataCache}
              userResponse={userResponse}
              isLoggedIn={isLoggedIn}
              getNewJWTorRedirectToPlatform={getNewJWTorRedirectToPlatform}
              settingData={settingData}
            />
          ),
        },
      ],
    },
  ]), [
    isPageLoading,
    updateTNC,
    dataCache,
    userResponse,
    isLoggedIn,
    getNewJWTorRedirectToPlatform,
    settingData,
    isMobileView,
    newMastheadEnabled,
    subNavMenu,
    searchIconEventHandler,
    isClicked,
    closeBtnEventHandler,
  ]);

  return (
    <div>
      <RouterProvider router={router} useTransitions={false} />
      <Suspense fallback={<Loader />}>
        <div className="hide-footer-visually">
          <Footer />
        </div>
      </Suspense>
    </div>
  );
}

export default function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [error, setError] = useState('');
  const [isPageLoading, setIsPageLoading] = useState<boolean>(true);
  const [userResponse, setUserResponse] = useState<UserResponse | null>(null);
  const [isTnCAccepted, setIsTnCAccepted] = useState(false);
  const [isTimeOutOfSync, setIsTimeOutOfSync] = useState(false);
  const [isClickedApp, setIsClickedApp] = useState<boolean>(false);

  // newMastheadEnabled is managed by DevRouting/ProdRouting components
  // Set to true as default for AppFeatureProvider context
  const newMastheadEnabled = true;

  const [settingData, setSettingData] = useState([
    { name: 'Accepted-Terms-And-Condition', value: false },
  ]);

  const checkCurrentUrl = () => {
    const currentUrl = window.location.href;
    const expectedPath = `/BoomiAI.html#ai;accountId=${accountId}`;
    return currentUrl.includes(expectedPath);
  };
  const goBackLinkHandler = useCallback(() => {
    const tempLink = document.createElement('a');
    tempLink.href = goBackLink;
    document.body.appendChild(tempLink);
    tempLink.click();
    document.body.removeChild(tempLink);
  }, [goBackLink]);
  const updateTNC = () => {
    setIsTnCAccepted(!isTnCAccepted);
  };
  const subNavMenu = getSubNavMenu();

  const getUser = () => {
    Rest.get(
      'com.boomi.platform.gwt.login.LoadUserHandler',
      'com.boomi.redsky.platform.gwt.common.LoadUserAction',
      { context: 'ATOMSPHERE' },
    )
      .then((response: any) => {
        setUserResponse(response as UserResponse);
        const initValue = response?.userPreferences?.agentStudioSettings;
        if (initValue && initValue !== '[]') {
          const parsedSettings = JSON.parse(initValue as string);
          setSettingData(parsedSettings);
        } else {
          setSettingData([
            { name: 'Accepted-Terms-And-Condition', value: false },
          ]);
        }
        setIsLoggedIn(true);
        setIsPageLoading(false);
      })
      .catch(() => {
        setIsLoggedIn(false);
      })
      .finally(() => {
        setIsPageLoading(false);
      });
  };

  const dataCache = useMemo(() => {
    const dc = new DataCache();
    if (userResponse) {
      dc.setCurrentUser(userResponse);
    }
    return dc;
  }, [userResponse]);

  const getNewJWTorRedirectToPlatform = useCallback(() => {
    setIsPageLoading(true);
    if (isDevelopmentMode()) {
      if (Util.getCookie('JWT')) {
        getUser();
      } else {
        setIsLoggedIn(false);
        setIsPageLoading(false);
      }
    } else {
      if (!Util.isValidToken()) {
        redirectToPlatformWrapper();
      }
      Auth.obtainJWTToken()
        .then((jwt) => {
          if (jwt) {
            Util.setCookie('JWT', jwt);
            getUser();
          }
        })
        .catch((err) => {
          setIsPageLoading(false);
          setError('Invalid ');
          if (
            err?.status
              === AppConstants.HTTP_STATUS_CODE.STATUS_401_UNAUTHORIZED
            || err?.status === AppConstants.HTTP_STATUS_CODE.STATUS_403_FORBIDDEN
          ) {
            redirectToPlatformWrapper();
          } else if (err?.status === AppConstants.HTTP_STATUS_CODE.STATUS_419_SESSION_EXPIRED) {
            setIsTimeOutOfSync(true);
          }
        });
    } // eslint-disable-next-line
  }, [isTnCAccepted]);

  useEffect(() => {
    getNewJWTorRedirectToPlatform();
  }, [getNewJWTorRedirectToPlatform]);

  const onTimeOutOfSyncDialogClose = useCallback(() => {
    window.location.reload();
  }, []);

  function searchIconEventHandler() {
    setIsClickedApp((prevState) => !prevState);
  }

  const closeBtnEventHandler = () => {
    setIsClickedApp(false);
  };

  if (isDevelopmentMode() && !isLoggedIn && !isPageLoading) {
    return (
      <Suspense fallback={<Loader />}>
        <Login
          error={error}
          isLoggedIn={isLoggedIn}
          setIsLoggedIn={setIsLoggedIn}
        />
      </Suspense>
    );
  }

  return (
    <BoomiContextProvider>
      <BoomiAgentFoundationContextProvider userResponse={userResponse}>
        <AppFeatureProvider
          value={{
            newMastheadEnabled,
          }}
        >
          <TimeOutOfSyncWarningMessage
            open={isTimeOutOfSync}
            onDialogClose={onTimeOutOfSyncDialogClose}
          />
          {isDevelopmentMode() ? (
            <DevRouting
              basePath={basePath}
              isPageLoading={isPageLoading}
              updateTNC={updateTNC}
              dataCache={dataCache}
              userResponse={userResponse}
              isLoggedIn={isLoggedIn}
              subNavMenu={subNavMenu}
              getNewJWTorRedirectToPlatform={getNewJWTorRedirectToPlatform}
              searchIconEventHandler={searchIconEventHandler}
              isClicked={isClickedApp}
              closeBtnEventHandler={closeBtnEventHandler}
              settingData={settingData}
            />
          ) : (
            <ProdRouting
              isPageLoading={isPageLoading}
              updateTNC={updateTNC}
              dataCache={dataCache}
              userResponse={userResponse}
              isLoggedIn={isLoggedIn}
              subNavMenu={subNavMenu}
              getNewJWTorRedirectToPlatform={getNewJWTorRedirectToPlatform}
              goBackLinkHandler={goBackLinkHandler}
              checkCurrentUrl={checkCurrentUrl}
              searchIconEventHandler={searchIconEventHandler}
              isClicked={isClickedApp}
              closeBtnEventHandler={closeBtnEventHandler}
              settingData={settingData}
            />
          )}
        </AppFeatureProvider>
      </BoomiAgentFoundationContextProvider>
    </BoomiContextProvider>
  );
}
